import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, MapPin, ShieldCheck, Truck, Package } from 'lucide-react';
import { COUNTRY_LANDINGS, GUIDES } from '../data/contentData';
import { getCountryEditorial } from '../data/editorialContent';
import { SUPPLIERS } from '../data/mockData';
import SupplierCard from '../components/SupplierCard';
import EditorialBriefing from '../components/EditorialBriefing';
import SEO, { siteUrl } from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import Img from '../components/Img';
import { track, EVENTS } from '../lib/analytics';

const CountrySourcing = () => {
  const { country } = useParams();
  const config = COUNTRY_LANDINGS[country?.toLowerCase()];

  useEffect(() => {
    if (!config) return;
    track(EVENTS.COUNTRY_LANDING_VIEWED, { slug: config.slug });
  }, [config?.slug]);

  if (!config) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center">
        <div className="label-overline">404</div>
        <h1 className="mt-4 font-display text-4xl tracking-tighter">Country page not found</h1>
        <Link to="/factories" className="mt-6 inline-block btn-giwahs btn-giwahs-primary">Back to directory</Link>
      </div>
    );
  }

  const suppliers = SUPPLIERS.filter(s => s.country.toLowerCase() === config.slug);
  const avgTrust = suppliers.length ? Math.round(suppliers.reduce((a, s) => a + s.trustScore, 0) / suppliers.length) : 0;
  const relatedGuides = GUIDES.filter(g => config.relatedGuides.includes(g.slug));
  const editorial = getCountryEditorial(config.slug);

  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: 'Factories', url: '/factories' },
    { name: `Manufacturers in ${config.name}`, url: `/manufacturers/${config.slug}` },
  ];

  return (
    <div data-testid="country-sourcing-page" className="surface-obsidian text-platinum">
      <SEO
        title={`Manufacturers in ${config.name} — Verified Factories Directory`}
        description={editorial?.intro?.[0] || config.intro}
        breadcrumbs={breadcrumbItems}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: config.h1,
          description: config.intro,
          url: `${siteUrl}/manufacturers/${config.slug}`,
          about: { '@type': 'Place', name: config.name },
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: suppliers.length,
            itemListElement: suppliers.slice(0, 10).map((s, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              item: { '@type': 'Organization', name: s.name, url: `${siteUrl}/supplier/${s.slug}` },
            })),
          },
        }}
      />
      {/* Breadcrumbs */}
      <div className="border-b border-hairline bg-[#0A0A0A]">
        <div className="mx-auto max-w-[1440px] px-5 py-3 md:px-8">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>
      {/* HERO */}
      <section className="border-b border-black bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
          <Link to="/factories" className="inline-flex items-center gap-2 label-overline text-neutral-300 hover:text-white">
            <ArrowLeft size={14} strokeWidth={1.5} /> Factory directory
          </Link>
          <div className="mt-8 flex items-center gap-3 label-overline text-neutral-400">
            <MapPin size={13} strokeWidth={1.5} /> Country Intelligence Brief
          </div>
          <h1 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 8vw, 7rem)', lineHeight: 0.9 }}>
            {config.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-300 md:text-lg">{config.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/rfq" className="btn-giwahs btn-giwahs-dark">Build an RFQ</Link>
            <Link to={`/factories?q=${encodeURIComponent(config.name)}`} className="btn-giwahs border border-white/30 bg-transparent text-white hover:bg-white hover:text-black">All {config.name} factories</Link>
          </div>
        </div>
      </section>

      {/* TRENDS STRIP */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 md:px-8">
          <div className="grid grid-cols-2 gap-px bg-neutral-200 md:grid-cols-4">
            {[
              ['Verified Factories', suppliers.length, Package],
              ['Avg Trust Score', `${avgTrust}/100`, ShieldCheck],
              ['MOQ Trend', config.moqTrend.split(' · ')[0], Package],
              ['Lead Time', config.leadTimeTrend, Truck],
            ].map(([l, v, I]) => (
              <div key={l} className="bg-white p-5">
                <I size={14} strokeWidth={1.5} className="text-neutral-400" />
                <div className="mt-3 label-overline text-neutral-500">{l}</div>
                <div className="mt-1 font-mono-data text-sm">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EDITORIAL BRIEFING */}
      <EditorialBriefing
        briefing={editorial}
        kicker={`/ Editorial briefing · ${config.name}`}
        title={`Sourcing from ${config.name} — at a glance.`}
        subjectName={config.name}
        relatedGuides={relatedGuides}
      />

      {/* STRENGTHS BY CATEGORY */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
          <span className="label-overline">/ 01 — Strengths by Category</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>What {config.name} does best.</h2>
          <div className="mt-10 grid gap-px bg-black sm:grid-cols-2 lg:grid-cols-3">
            {config.strengths.map((s, i) => (
              <div key={s.cat} className="bg-white p-6" data-testid={`strength-${i}`}>
                <span className="marker-num text-neutral-500">/ {String(i + 1).padStart(2, '0')}</span>
                <div className="mt-5 font-display text-2xl tracking-tight" style={{ fontWeight: 600 }}>{s.cat}</div>
                <p className="mt-3 text-sm leading-relaxed text-neutral-700">{s.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CERT TRENDS */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] grid gap-10 px-5 py-12 md:grid-cols-2 md:px-8 md:py-16">
          <div>
            <span className="label-overline">/ 02 — Common Certifications</span>
            <h3 className="mt-3 font-display text-2xl tracking-tighter md:text-3xl" style={{ fontWeight: 600 }}>Certification trends.</h3>
            <div className="mt-5 flex flex-wrap gap-2">
              {config.commonCerts.map(c => <span key={c} className="giwahs-chip-dark font-mono-data text-xs">{c}</span>)}
            </div>
          </div>
          <div>
            <span className="label-overline">/ 03 — MOQ Reality</span>
            <h3 className="mt-3 font-display text-2xl tracking-tighter md:text-3xl" style={{ fontWeight: 600 }}>What buyers should expect.</h3>
            <p className="mt-4 font-mono-data text-sm text-neutral-700">{config.moqTrend}</p>
            <p className="mt-2 font-mono-data text-sm text-neutral-700">Lead time: {config.leadTimeTrend}</p>
          </div>
        </div>
      </section>

      {/* FEATURED SUPPLIERS */}
      {suppliers.length > 0 && (
        <section className="border-b border-hairline surface-obsidian">
          <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
            <span className="label-overline">/ 04 — Verified Suppliers in {config.name}</span>
            <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>The directory.</h2>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {suppliers.slice(0, 6).map((s, i) => <SupplierCard key={s.id} supplier={s} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* RELATED GUIDES */}
      {relatedGuides.length > 0 && (
        <section className="border-b border-hairline surface-obsidian">
          <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
            <span className="label-overline">/ 05 — Related Sourcing Guides</span>
            <div className="mt-6 grid gap-5 md:grid-cols-3">
              {relatedGuides.map(g => (
                <Link key={g.slug} to={`/sourcing-guides/${g.slug}`} className="group border border-black bg-white">
                  <div className="aspect-[16/10] overflow-hidden"><Img src={g.cover} alt={`${g.title} — guide`} variant="card" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                  <div className="p-5">
                    <span className="label-overline">{g.categoryLabel}</span>
                    <div className="mt-3 font-display text-lg tracking-tight" style={{ fontWeight: 600 }}>{g.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <div className="grid items-end gap-10 md:grid-cols-2">
            <h2 className="font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.25rem, 5vw, 4.5rem)', lineHeight: 0.95 }}>
              Source from {config.name}? Submit an RFQ.
            </h2>
            <div className="flex md:justify-end">
              <Link to="/rfq" data-testid="country-final-rfq" className="btn-giwahs btn-giwahs-dark inline-flex items-center gap-2">
                Build my RFQ <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CountrySourcing;
